import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && user.role === "admin") {
    const to = loc.state?.from?.pathname || "/admin";
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        setError("This account is not an admin.");
        return;
      }
      nav(loc.state?.from?.pathname || "/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="flex items-center gap-2 text-stone-500 text-[11px] tracking-[0.3em] uppercase font-semibold">
          <Lock size={13} /> Globezing · Admin
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light leading-tight mt-4 text-stone-900">
          Welcome back,
          <br /><em className="italic">writer.</em>
        </h1>
        <p className="mt-4 text-stone-600 text-[15px]">Sign in to publish new stories, edit the archive, and upload photographs and video.</p>

        <form onSubmit={onSubmit} data-testid="admin-login-form" className="mt-10 space-y-7">
          <label className="block">
            <span className="kicker text-stone-500">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="input-editorial mt-2"
              placeholder="ashwini@globezing.com"
              required
              autoFocus
            />
          </label>
          <label className="block">
            <span className="kicker text-stone-500">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="input-editorial mt-2"
              placeholder="••••••••••"
              required
            />
          </label>

          {error && (
            <p data-testid="login-error" className="text-sm text-[#9c4122] bg-[#fbeae6] px-4 py-2 border-l-2 border-[#9c4122]">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full inline-flex items-center justify-center gap-3 bg-stone-900 text-[#FDFBF7] px-8 py-4 text-[12px] tracking-[0.25em] uppercase font-semibold hover:bg-[#9c4122] transition-colors disabled:opacity-50"
          >
            {loading ? <><Loader2 className="animate-spin" size={16} /> Signing in…</> : "Sign in"}
          </button>
        </form>
      </motion.div>
    </main>
  );
}
